import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  currentDisplayCurrency,
  setDisplayCurrency,
  supportedDisplayCurrencies,
} from '@/lib/displayCurrency';
import { Dropdown, DropdownItem } from '@/components/ui/Dropdown';

const CURRENCY_NAMES: Record<string, { name: string; symbol: string; flag?: string }> = {
  USD: { name: 'US Dollar', symbol: '$', flag: '🇺🇸' },
  JPY: { name: '日本円', symbol: '¥', flag: '🇯🇵' },
  EUR: { name: 'Euro', symbol: '€', flag: '🇪🇺' },
  GBP: { name: 'Pound', symbol: '£', flag: '🇬🇧' },
  CAD: { name: 'Canadian Dollar', symbol: 'CA$', flag: '🇨🇦' },
  AUD: { name: 'Australian Dollar', symbol: 'A$', flag: '🇦🇺' },
  KRW: { name: '대한민국 원', symbol: '₩', flag: '🇰🇷' },
  CNY: { name: '人民币', symbol: '¥', flag: '🇨🇳' },
  HKD: { name: 'HK Dollar', symbol: 'HK$', flag: '🇭🇰' },
  SGD: { name: 'Singapore Dollar', symbol: 'S$', flag: '🇸🇬' },
  TWD: { name: 'TW Dollar', symbol: 'NT$', flag: '🇹🇼' },
  INR: { name: 'Indian Rupee', symbol: '₹', flag: '🇮🇳' },
  BRL: { name: 'Real', symbol: 'R$', flag: '🇧🇷' },
  MXN: { name: 'Mexican Peso', symbol: 'MX$', flag: '🇲🇽' },
};

/**
 * Polished currency picker. Replaces the native `<select>` so the
 * symbol + flag + native currency name render consistently (instead
 * of the browser's default OS-themed select), with full keyboard nav
 * + dark mode + Knowlez design tokens.
 */
export function CurrencySwitcher({ onChange }: { onChange?: (code: string) => void }) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(currentDisplayCurrency());

  const items: DropdownItem<string>[] = supportedDisplayCurrencies().map((code) => {
    const meta = CURRENCY_NAMES[code];
    return {
      value: code,
      label: code,
      hint: meta?.name,
      leading: (
        <span aria-hidden className="inline-flex items-center gap-1 num text-[12px] text-ink-500 dark:text-ink-400 min-w-[18px]">
          {meta?.flag || meta?.symbol || ''}
        </span>
      ),
    };
  });

  return (
    <Dropdown
      value={current}
      onChange={(v) => {
        setDisplayCurrency(v);
        setCurrent(v);
        onChange?.(v);
      }}
      items={items}
      ariaLabel={t('common.currency', { defaultValue: 'Currency' }) as string}
      size="sm"
      width={130}
    />
  );
}
